import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { ProductMark } from '../components/ProductMark';
import { appDocumentTitle } from '../appMeta';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import styles from './authLayout.module.scss';

export function LoginPage() {
  useDocumentTitle(appDocumentTitle('Sign in'));
  const { login, loginPending } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromRegister = (location.state as { message?: string } | null)?.message;

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await login(identifier.trim(), password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    }
  }

  return (
    <div className={styles.wrap}>
      <Card className={styles.card} elevation={6}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <ProductMark />
          <Typography variant="h5" component="h1" gutterBottom>
            Sign in
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use the account approved by your Strapi admin.
          </Typography>
          {fromRegister && (
            <Alert severity="info" sx={{ mb: 2 }} data-testid="login-post-register-info">
              {fromRegister}
            </Alert>
          )}
          <Box component="form" onSubmit={onSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Email or username"
                autoComplete="username"
                value={identifier}
                onChange={(ev) => setIdentifier(ev.target.value)}
                required
                fullWidth
                inputProps={{ 'data-testid': 'login-identifier' }}
              />
              <TextField
                label="Password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                required
                fullWidth
                inputProps={{ 'data-testid': 'login-password' }}
              />
              {error && (
                <Alert severity="error" variant="outlined" data-testid="login-error">
                  {error}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loginPending}
                fullWidth
                data-testid="login-submit"
              >
                {loginPending ? 'Signing in…' : 'Sign in'}
              </Button>
            </Stack>
          </Box>
          <Typography variant="body2" color="text.secondary" className={styles.footer}>
            No account?{' '}
            <Link component={RouterLink} to="/register" data-testid="login-register-link">
              Register
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </div>
  );
}
